<script>
    import {createEventDispatcher} from 'svelte';
    import debounce from './debounce';
    import BuildingCardV2 from '$components/building-card/BuildingCardV2.svelte';
    import BuildingCardSkeleton from '$components/building-card/BuildingCardSkeleton.svelte';
    import AddBuildingPrompt from '$components/AddBuildingPrompt.svelte';
    import searchEntry from '$lib/stores/searchEntry';

    const dispatch = createEventDispatcher();

    export let subLocalities = [];
    export let queryString = '';
    export let showInitialMessage = false;
    export let showResultCount = false;
    export let highlightSelected = false;
    export let showAddBuildingPrompt = false;

    let queryParameters = {
        queryString,
        filters: {},
    };
    let results = [];
    let buildings = [];
    let isLoading = false;
    let isInitial = true;
    let selectedUuid = '';

    // $: ({ results: buildings = [] } = results);
    $: (queryParameters = {queryString, filters: queryParameters.filters});

    function call() {
        if (queryString.length === 0) {
            buildings = [];
            return;
        }

        isInitial = false;
        isLoading = true;

        fetch('/api/v2/search', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(queryParameters),
        })
            .then(res => res.json())
            .then(res => {
                setTimeout(() => {
                    isLoading = false;
                }, 50);
                buildings = res.results;
            });
    }

    $: if (queryString.length > 1) {
        call();
    }

    function handleClickResult(uuid) {
        if (highlightSelected) {
            selectedUuid = uuid;
        }
        dispatch('clickSearchResult', uuid);
    }
</script>

<section class='flex flex-col md:flex-row w-full md:gap-10'>
    <section class='flex flex-col mr-5 w-fit md:w-1/3'>
        <label class='label text-lg font-bold' for='queryString'>
            Find your building
            <input name='queryString' id='queryString' bind:value={queryString} type='text'
                   use:debounce={{queryString, func: call, duration: 500}}
                   class='input font-light bg-neutral-100 hover:bg-surface-600 px-2 py-1.5 rounded-xl'
                   placeholder='Search by Building Name or Address...'>
        </label>
    </section>
    <div class='w-full md:w-2/3 mt-14 md:mt-0'>
        <h4 class='font-bold text-lg'>Matching results</h4>
        <div class='flex flex-row items-center justify-start gap-5'>
            <label for='sublocality' class='label flex-grow-1 min-w-fit'>Filter results by area:</label>
            <select class='flex w-fit font-light text-primary-500 appearance-none bg-white' id='sublocality'
                    bind:value={queryParameters.filters.sublocality} on:change={call}>
                <option value={null}>All Areas</option>
                {#each subLocalities as subLocality}
                    <option value={subLocality.id}>{subLocality.name}</option>
                {/each}
            </select>
        </div>
        {#if showResultCount}
            {#if buildings.length === 0 && !isLoading && showInitialMessage}
                <p class='mt-5 text-neutral-500'>{isInitial ? 'Type in a name or address of a building in the search box.' : 'No results found.'}</p>
            {:else if buildings.length === 1}
                <p class='mt-1 text-neutral-500'>1 result</p>
            {:else if buildings.length > 1}
                <p class='mt-1 text-neutral-500'>{buildings.length} results</p>
            {/if}
        {/if}
        <p></p>
        {#if isLoading}
            <section class='grid gap-1 md:gap-4 grid-cols-1 sm:grid-cols-2 grid-rows-3 w-full'>
                <BuildingCardSkeleton/>
                <BuildingCardSkeleton/>
                <BuildingCardSkeleton/>
                <BuildingCardSkeleton/>
            </section>
        {:else}
            <section class='grid gap-1 md:gap-4 grid-cols-1 sm:grid-cols-2 grid-rows-2 sm:grid-rows-1 w-full'>
                {#each results as result}
                    {#if 'name' in result.highlight}
                        <span>{@html result.highlight.name.snippet}</span>
                    {:else}
                        <span>{result.document.name}</span>
                    {/if}
                    {#if 'address' in result.highlight}
                        <span>{@html result.highlight.address.snippet}</span>
                    {:else}
                        <span>{result.document.address}</span>
                    {/if}
                {/each}
                {#each buildings as building}
                    <div tabindex='0'
                         role='button'
                         on:keypress={() => {handleClickResult(building.uuid)}} class='w-full hover:cursor-pointer'
                         on:click={() => {handleClickResult(building.uuid)}}>
                        <BuildingCardV2 name='{building.name}'
                                        address='{building.address}'
                                        neighbourhood='{building.neighbourhood}'
                                        showFooter='{false}'
                                        highlight={selectedUuid === building.uuid}
                                        aggregatedScore={building.review_score > 0 ? {score: building.review_score}: null}
                        />
                    </div>
                {/each}
                {#if showAddBuildingPrompt && !isInitial}
                    <div class='mt-10 block md:hidden'>
                        <AddBuildingPrompt on:addBuildingRedirect={() => ($searchEntry = queryString)}
                                           class='text-center justify-center'/>
                    </div>
                {/if}
            </section>
            {#if showAddBuildingPrompt && !isInitial}
                <div class='hidden md:block mt-10'>
                    <AddBuildingPrompt on:addBuildingRedirect={() => ($searchEntry = queryString)}
                                       class='text-center justify-center'/>
                </div>
            {/if}
        {/if}
    </div>

</section>

<div class='hidden'>
    <pre>{JSON.stringify(results, null, 2)}</pre>
    <pre>{JSON.stringify(buildings, null, 2)}</pre>
</div>